import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { i18nResources } from "./resources";
import {
    LANGUAGE_COOKIE_NAME,
    MAIN_LANGUAGE_STORAGE_KEY,
    migrateLanguageCookieToSharedDomain,
    normalizeLanguageCode,
    persistLanguagePreference,
} from "./languageSettings";

migrateLanguageCookieToSharedDomain(MAIN_LANGUAGE_STORAGE_KEY);

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: i18nResources,
        fallbackLng: "en",
        // Region subtags must stay lowercase. i18next's formatLanguageCode otherwise
        // rewrites "pt-pt" -> "pt-PT", which misses our lowercase resource key and
        // silently falls back to the base language — serving Brazilian Portuguese to
        // the European locale.
        lowerCaseLng: true,
        debug: import.meta.env.DEV,
        initImmediate: false,
        interpolation: {
            escapeValue: false, // React already escapes
        },
        react: {
            useSuspense: false,
        },
        detection: {
            order: ["cookie", "querystring", "localStorage", "navigator"],
            caches: [],
            lookupCookie: LANGUAGE_COOKIE_NAME,
            lookupQuerystring: "lang",
            lookupLocalStorage: MAIN_LANGUAGE_STORAGE_KEY,
        },
    });

const applyDocumentLanguage = (lng?: string) => {
    if (!lng || typeof document === "undefined") return;
    const base = lng.split("-")[0];
    document.documentElement.lang = base;
    document.documentElement.dir = base === "ar" ? "rtl" : "ltr";
};

const syncMainLanguage = (lng?: string) => {
    if (!lng) return;
    applyDocumentLanguage(lng);
    persistLanguagePreference(lng, MAIN_LANGUAGE_STORAGE_KEY);
};

syncMainLanguage(i18n.resolvedLanguage || i18n.language);
i18n.on("languageChanged", syncMainLanguage);

// The journal and Boona Direct (apps/inbox, admin.boona.app/inbox/) both load this file on
// one origin, so they share localStorage: a language picked in one open tab follows in the
// other without a reload. (setItem with an unchanged value fires no event — no loop.)
if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
        if (event.key !== MAIN_LANGUAGE_STORAGE_KEY || !event.newValue) return;
        const next = normalizeLanguageCode(event.newValue);
        if (next && next !== (i18n.resolvedLanguage || i18n.language)) void i18n.changeLanguage(next);
    });
}

export default i18n;
